/*
 * ORDER MANAGE — the customer's actions on one order, for every theme:
 * change the delivery address / pickup point, change the size (another
 * variant, same price), change the products before the order is accepted
 * (remove, change quantity, add - the difference paid online or refunded),
 * cancel, refund method, message about the order, and return / exchange /
 * late return (with the return form of Prodyflow settings/returns).
 *
 * All logic is here: data, rules, API calls, validation, errors, the question
 * of every step, the button labels, the menu list. The markup is the engine's
 * too (resources/views/order-manage, rendered by App\Helpers\OrderManageView,
 * `{{ order_manage() }}` in a Twig page); a theme only overrides the parts it
 * wants to look different, like the checkout: layout/order-manage/<part>.twig
 * (head, menu, address, variant, cancel, contact, items-edit, items-add, items-pay,
 * items-summary, items, offer, ship, refund, summary, done, foot, and the shell).
 * The texts are the lang group order_manage. A part is the in-DOM Vue template:
 *
 *   <div data-order-manage-app
 *        data-is-change="1|0" data-is-return="1|0"   sale channel settings
 *        data-help-url="/hu/shop/profile/help"        where a return that cannot start here goes on
 *        data-texts="#id-of-a-json-script"            the texts (e_pick, l_ret, m_pay, ...)
 *        data-delimiters="[[,]]">                     optional, default [[ ]] ({{ }} is Twig's)
 *     ...Vue template, using the data / computed / methods below...
 *   </div>
 *
 * Openers anywhere on the page: [data-order-manage] (the action menu) or
 * [data-return-open] (the return flow), with data-order-id and an optional
 * data-flow. The theme decides how the dialog opens / closes:
 *   OrderManage.setup({ open() {...}, close() {...} })
 * Events on document: order-manage:mounted, order-manage:done.
 *
 * Endpoints (engine → Prodyflow): /api/orders, /api/orders/{id}/delivery/edit,
 * /api/package-points/..., /api/orders/item/{id}/change | remove,
 * /api/orders/{id}/deny, /api/orders/{id}/returnmethods, /api/orders/{id}/refound,
 * /api/orders/return-questions, /api/countries/deliveries/{country}/returns,
 * /api/orders/return-settings ("ship it myself" and the return address),
 * /api/package-pickups, /api/contact-categories, /api/contact.
 * Changing the products: /api/search?is_help_page=1, /api/payment-methods,
 * /api/orders/{id}/payment (+ the payment app's script, which calls
 * window.CART.startPayment / finishPayment / finishPaymentButton; it renders
 * into the element with id "order-manage-payment"), then
 * /api/orders/item/{id}/add | remove | change. As before, an online payment
 * is taken before the change is sent.
 * An exchange's return parcel is free (the backend prices it 0).
 * There is no payment in this flow: a pure return only offers drop-off
 * methods that cost no more than the refund.
 * "Ship it myself" (settings/returns): a pure return may be sent by the
 * customer on their own, at their own cost - it is the ship option with
 * `self: true` (no courier, no address); the theme shows `selfAddress`.
 */
const OrderManage = (() => {
    const DEFAULT_TEXTS = {
        e_pick: 'Select a product.',
        e_dispute: 'Please write why you are returning it now (at least 20 characters).',
        e_reason: 'Please answer the questions.',
        e_choice: 'Choose an option for every product.',
        e_size: 'Choose a size.',
        e_method: 'Choose a drop-off method.',
        e_addr: 'Please give the pickup address.',
        e_addr2: 'Please give the delivery address.',
        e_time: 'Choose a pickup time.',
        e_point: 'Choose a pickup point.',
        e_contact: 'Please write your message.',
        e_field: 'Please fill in the required fields.',
        contact_attach: 'Attach files',
        contact_attach_hint: 'up to 3 files',
        e_refund: 'Choose a refund method.',
        e_bank: 'Please give the bank details.',
        e_load: 'Could not load the data. Please try again.',
        e_send: 'Could not send it. Please try again.',
        e_items: 'Change something first: a quantity, a removed or an added product.',
        e_payment: 'Choose a payment method.',
        e_pay_form: 'Please complete the payment.',
        self_name: 'I\'ll ship it myself',
        self_desc: 'You send the parcel on your own, at your own cost.',
    };

    const canAnimate = () => !document.hidden && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fill = (tpl, v) => String(tpl || '').replace(/:(size|price|days|id)/, v);
    const token = () => (window.Cookies && Cookies.getItem('login_token')) || '';
    const headers = () => (window.http && http.headers ? http.headers() : { 'Content-Type': 'application/json', Accept: 'application/json' });
    const post = (url, body) => fetch(url, { method: 'POST', headers: headers(), body: JSON.stringify(Object.assign({ token: token() }, body || {})) });

    const loadScript = (src, after) => {
        const tag = document.createElement('script');
        tag.src = src;
        if (after) tag.onload = () => loadScript(after);
        document.body.appendChild(tag);
    };
    // the payment apps talk to window.CART; while this flow takes a payment it
    // answers them, then the page's own CART (if any) comes back
    let savedCart;
    const claimCart = (vm) => {
        if (savedCart === undefined) savedCart = window.CART;
        // everything the payment apps call on CART (the checkout's and the payment page's one has the same): without
        // paymentLook / paymentTarget the card form (Revolut, Stripe) throws before it is created and takePayment()
        // gets no finish handler: "Please complete the payment details."
        window.CART = {
            initializePaymentButton() {},
            startPayment: (event) => vm.payStart(event),
            finishPayment: (event) => vm.payFinish(event),
            finishPaymentButton: (event) => vm.payFinishButton(event),
            // the theme's look options of a payment widget ({} = the app's own look; the Orders page gives none)
            paymentLook: (name) => (window.PAY_LOOK && window.PAY_LOOK[name]) || {},
            // the widget is mounted into this flow's own box
            paymentTarget: (id) => document.getElementById(id),
            // a wallet button in place of the order button (the checkout's standalone mode): not used here
            paymentDone() {},
            billingCountryCode: () => String(window.PAY_COUNTRY || '').toUpperCase(),
            // where the payment app's return page goes back to: /payment/{id}?token=...
            get orderId() { return vm.order.id + '?token=' + (vm.order.token || ''); },
        };
    };
    const releaseCart = () => {
        if (savedCart === undefined) return;
        window.CART = savedCart;
        savedCart = undefined;
    };

    let hooks = { open: null, close: null };
    let app = null;
    let root = null;
    // the element the app is mounted on (a root component with several children has no element of its own)
    let mountEl = null;

    let textsCache = null;
    const readTexts = (el) => {
        if (textsCache) return textsCache;
        let t = {};
        try {
            const src = el.dataset.texts ? document.querySelector(el.dataset.texts) : null;
            t = src ? JSON.parse(src.textContent) : {};
        } catch (e) { t = {}; }
        textsCache = Object.assign({}, DEFAULT_TEXTS, t);
        return textsCache;
    };

    // the Orders list's root component (already loaded orders, refresh after a change)
    const ordersRoot = () => {
        const a = window.ORDERS && ORDERS.vue;
        if (!a) return null;
        if (a._instance) return a._instance.proxy;
        const v = a._container && a._container._vnode;
        return (v && v.component && v.component.proxy) || null;
    };
    const findOrder = async (id) => {
        try {
            const r0 = ordersRoot();
            const list = r0 && r0.orders;
            const hit = list && list.data && list.data.find((o) => String(o.id) === String(id));
            if (hit) return JSON.parse(JSON.stringify(hit));
        } catch (e) { /* load it */ }
        const r = await post('/api/orders');
        if (r.status === 401) { location.href = (window.SHOP_BASE || '') + '/login'; return null; }
        const d = (await r.json()).data || {};
        const lists = [(d.all_orders && d.all_orders.data) || [], d.pending_orders || [], d.recent_orders || [], d.refund_orders || []];
        for (const list of lists) {
            const hit = list.find((o) => String(o.id) === String(id));
            if (hit) return hit;
        }
        return null;
    };

    /*
     * One order item: its variant-forming characteristics by their settings
     * (the main one, e.g. colour, stays; the other one, e.g. size, can change),
     * and the other variants of the same main value, in the backend's order.
     */
    const toItem = (oi, lan) => {
        const pv = oi.productVariant || {};
        const tr = (pv.product && pv.product.product_translations) || [];
        const name = (tr.find((x) => x.language_code === lan) || tr[0] || {}).name || '';
        const vars = oi.other_variants || [];
        const orig = vars.find((v) => v.is_original) || vars.find((v) => v.product_variant_id === oi.product_variant_id) || null;
        // the characteristic that differs between the variants (settings: is_variant, not main)
        const own = (pv.characteristics || []);
        const flagged = own.find((c) => c.is_variant && !c.main_characteristics);
        const changeType = (() => {
            if (flagged) return flagged.type_id;
            if (!orig || vars.length < 2) return null;
            const c = (orig.characteristics || []).find((ch) => vars.some((v) => (v.characteristics || []).some((x) => x.type_id === ch.type_id && x.value_id !== ch.value_id)));
            return c ? c.type_id : null;
        })();
        const valueOf = (v) => ((v.characteristics || []).find((c) => c.type_id === changeType) || {}).value || '';
        const same = (v) => (v.characteristics || []).every((c) => c.type_id === changeType || ((orig && orig.characteristics) || []).some((o) => o.type_id === c.type_id && o.value_id === c.value_id));
        const sizes = changeType === null ? [] : vars.filter(same)
            .map((v) => ({ pv: v.product_variant_id, label: valueOf(v), ok: v.available !== false && (v.stock === undefined || v.stock > 0), orig: v === orig }));
        const other = orig ? (orig.characteristics || []).filter((c) => c.type_id !== changeType).map((c) => c.value).filter(Boolean) : [];
        const origSize = orig ? valueOf(orig) : '';
        return {
            id: oi.id,
            pv: oi.product_variant_id,
            qty: Number(oi.quantity) || 0,
            price: Number(oi.payed_price) || 0,
            name,
            url: oi.url || null,
            img: (pv.productImages && pv.productImages[0]) || '',
            productId: pv.product_id || (pv.product && pv.product.id) || null,
            origSize,
            other,
            sizes,
        };
    };

    const component = (el) => {
        const t = readTexts(el);
        const cfg = { isChange: el.dataset.isChange === '1', isReturn: el.dataset.isReturn === '1' };
        // the Help page (data-help-url of the shell), where a return that cannot start here goes on
        const helpUrl = el.dataset.helpUrl || (window.SHOP_BASE || '') + '/profile/help';
        return {
            data() {
                return {
                    t, cfg, helpUrl,
                    questions: [],
                    order: null, mode: 'return', lan: window.lan || document.documentElement.lang || 'hu',
                    items: [], pick: {}, disputeText: '',
                    step: 'items', dir: 1, done: false, anim: true,
                    methods: null, method: null, quote: null, addr: AddressFields.blank(), addrCountry: null,
                    returnSettings: null,
                    days: [], day: null, hour: null,
                    refundMethods: null, rf: null, currency: '', bank: {},
                    error: '', sending: false,
                    flow: 'return', fromMenu: false,
                    sizePv: {},
                    pointQuery: '', points: null, point: null,
                    daddr: AddressFields.blank(), noErrors: {},
                    categories: null, category: null, topicPicked: false, contactText: '', fields: {}, files: [],
                    // changing the products
                    cart: [], q: '', results: [], resultCount: 0, searching: false,
                    payments: null, payment: null, payError: '', payReady: false,
                    paymentData: null, payEvents: { start: null, finish: null, finishButton: null }
                };
            },
            computed: {
                exchange() { return this.cfg.isChange && this.mode === 'return'; },
                picked() { return this.items.filter((it) => this.pick[it.id] && this.pick[it.id].on); },
                swaps() { return this.exchange ? this.picked.filter((it) => this.pick[it.id].choice === 'swap' && this.pick[it.id].pv) : []; },
                swapCount() { return this.swaps.length; },
                // the exchange is offered for the items whose answers ask for it (a change_variant / replace_same option); a return
                // form without any such option offers it for every returned item, as before
                hasActions() { return this.questions.some((q) => (q.options || []).some((o) => o.action)); },
                offerItems() { return this.exchange ? this.picked.filter((it) => this.needsOffer(it)) : []; },
                returnsValue() {
                    return this.picked.filter((it) => !this.swaps.includes(it)).reduce((s, it) => s + it.price * this.pick[it.id].qty, 0);
                },
                needRefund() { return this.picked.length > this.swaps.length; },
                steps() {
                    if (this.flow === 'menu') return ['menu'];
                    if (this.flow === 'cancel') return this.paid ? ['cancel', 'refund'] : ['cancel'];
                    if (this.flow === 'items') {
                        const s = ['items_edit', 'items_add'];
                        if (this.itemsDiff > 0) s.push('items_pay');
                        else if (this.itemsDiff < 0 && this.paid) s.push('refund');
                        s.push('items_summary');
                        return s;
                    }
                    if (this.flow !== 'return') return [this.flow];
                    const s = ['items'];
                    if (this.exchange && this.offerItems.length) s.push('offer');
                    s.push('ship');
                    if (this.needRefund) s.push('refund');
                    s.push('summary');
                    return s;
                },
                stepIndex() { return Math.max(0, this.steps.indexOf(this.step)); },
                isLast() { return this.stepIndex === this.steps.length - 1; },
                state() { return this.order ? OrderRules.state(this.order) : {}; },
                win() { return OrderRules.returnWindow(this.order); },
                del() { return (this.order && this.order.orderDeliveries && this.order.orderDeliveries[0]) || {}; },
                paid() { return !!this.order && OrderRules.isPaid(this.order); },
                canModify() { return OrderRules.canModify(this.order); },
                canReturn() { return OrderRules.canReturn(this.order, this.cfg); },
                sizeItems() { return this.items.filter((it) => it.sizes.some((x) => x.ok && !x.orig)); },
                // changing the products: what stays, what is removed, what is added
                cartKept() { return this.cart.filter((c) => c.qty > 0); },
                cartRemoved() { return this.cart.filter((c) => !c.added && c.qty < c.origQty); },
                cartAdded() { return this.cart.filter((c) => c.added || c.qty > c.origQty); },
                itemsAdd() {
                    return this.cart.filter((c) => c.qty > c.origQty).map((c) => ({ id: c.pv, quantity: c.qty - c.origQty }));
                },
                itemsDel() {
                    return this.cart.filter((c) => c.qty < c.origQty).map((c) => ({ id: c.pv, quantity: c.origQty - c.qty }));
                },
                itemsChanged() { return this.itemsAdd.length > 0 || this.itemsDel.length > 0; },
                // + to pay, - to refund
                itemsDiff() { return this.cart.reduce((s, c) => s + c.price * (c.qty - c.origQty), 0); },
                paymentMethod() { return (this.payments || []).find((p) => p.id === this.payment) || null; },
                // payment type 1 is taken online, here, before the change is sent
                onlinePayment() { return !!this.paymentMethod && Number(this.paymentMethod.type) === 1; },
                pointMode() { return !!this.del.package_point || (this.del.delivery_method && this.del.delivery_method.type == 2); },
                // the action menu, labelled with the theme's texts (m_<key>, m_<key>_s)
                actions() {
                    return OrderRules.actions(this.order, this.cfg).map((a) => Object.assign({}, a, {
                        label: this.t['m_' + a.key] || a.key,
                        sub: a.detail || this.t['m_' + a.key + '_s'] || '',
                    }));
                },
                /*
                 * What the menu step lists: `actions`, plus a tracking link for a refused parcel,
                 * and the invoices grouped (the first one, the modifying ones - one, or a group
                 * that opens with `order._invM`). Every row: key, label, sub, href, blank, n, group, nested.
                 */
                menuActions() {
                    const o = this.order;
                    if (!o) return [];
                    const t = this.t;
                    const list = this.actions;
                    const d0 = (o.orderDeliveries || [])[0] || {};
                    const refused = o.is_refused || (o.after_status && o.after_status.is_refused);
                    const track = refused && !list.some((x) => x.key === 'track') && d0.public_tracking_code
                        ? [{ key: 'track', blank: true, href: '/track/' + d0.app_id + '/' + d0.public_tracking_code, label: t.m_track || 'Track', sub: d0.public_tracking_code }]
                        : [];
                    return track.concat(list).flatMap((x) => {
                        if (x.key !== 'invoice') return [x];
                        const ts = (i) => { const v = Date.parse(String(i.invoice.invoice_date || '').replace(' ', 'T')); return isNaN(v) ? null : v; };
                        const amt = (i) => { const v = i.invoice.payed_amount ?? i.invoice.original_amount; return v === null || v === undefined || v === '' ? null : Number(v); };
                        const chrono = (o.orderInvoices || []).filter((i) => i && i.invoice && i.invoice.id).slice().reverse()
                            .map((i, k) => ({ i, k }))
                            .sort((a, b) => { const p = ts(a.i), q = ts(b.i); return p !== null && q !== null && p !== q ? p - q : a.k - b.k; })
                            .map((v) => v.i);
                        // the original invoice (the first with a positive amount) leads
                        const oi = chrono.findIndex((i) => amt(i) !== null && amt(i) >= 0);
                        const all = oi > 0 ? [chrono[oi]].concat(chrono.filter((_, k) => k !== oi)) : chrono;
                        if (all.length < 2) return [all.length ? { ...x, sub: all[0].invoice.invoice_number || x.sub } : x];
                        const row = (i, n, label, sub) => ({ ...x, n, href: '/invoice/' + i.invoice.id + '/?token=' + i.invoice.invoice_file, label, sub });
                        const first = row(all[0], 0, t.m_invoice_first || x.label, all[0].invoice.invoice_number || '');
                        const mods = all.slice(1);
                        if (mods.length === 1) return [first, row(mods[0], 1, t.m_invoice_new || x.label, mods[0].invoice.invoice_number || '')];
                        const head = { ...x, n: 'g', group: true, href: null, blank: false, label: t.m_invoice_mods || x.label, sub: String(t.m_invoice_count || ':n').replace(':n', mods.length) };
                        return [first, head].concat(o._invM ? mods.map((i, k) => ({ ...row(i, k + 1, i.invoice.invoice_number || ('#' + (k + 1)), ''), nested: true })) : []);
                    });
                },
                // the small heading above the title: which flow this is, and the step count
                kicker() {
                    const t = this.t;
                    if (this.flow !== 'return') return t.manage_title;
                    if (this.mode === 'dispute') return t.dispute_title;
                    return this.exchange ? t.title_change : t.title;
                },
                // the question the current step asks (or what was done)
                title() {
                    const t = this.t;
                    if (this.done) {
                        const d = { address: 'done_address', size: 'done_size', items: 'done_items', cancel: 'done_cancel', refund: 'done_refund', contact: 'done_contact' };
                        return t[d[this.flow] || 'done_title'];
                    }
                    const s = this.step;
                    if (s === 'address') return this.pointMode ? t.q_point : t.q_address;
                    if (s === 'items') return this.mode === 'dispute' ? t.q_dispute : t.q_items;
                    const q = { menu: 'q_menu', size: 'q_size', items_edit: 'q_items_edit', items_add: 'q_items_add', items_pay: 'q_items_pay', items_summary: 'q_summary', cancel: 'q_cancel', contact: 'q_contact', offer: 'q_offer', ship: 'q_ship', refund: 'q_refund' };
                    return t[q[s] || 'q_summary'];
                },
                // the main button of the footer
                nextLabel() {
                    const t = this.t, f = this.flow;
                    if (f === 'items' && this.isLast && this.itemsDiff > 0 && this.onlinePayment) return t.pay_save;
                    if ((f === 'items' && this.isLast) || ['address', 'size', 'refund'].includes(f)) return t.save;
                    if (f === 'cancel' && this.isLast) return t.cancel_btn;
                    if (f === 'contact') return t.send;
                    if (this.step !== 'summary') return t.next;
                    if (this.mode === 'dispute') return t.send_dispute;
                    if (this.swapCount && !this.needRefund) return t.send_swap;
                    return t.send_return;
                },
                // the text on the done screen
                doneText() {
                    const t = this.t;
                    if (this.flow === 'contact') return t.done_contact_text;
                    if (this.flow !== 'return') return t.done_text;
                    return this.mode === 'dispute' ? t.done_text_late : t.done_text_return;
                },
                // what happens next after a return (the done screen's timeline)
                timeline() {
                    if (this.flow !== 'return') return [];
                    const t = this.t;
                    const l = [t.t1];
                    if (this.mode === 'dispute') l.push(t.t_review);
                    l.push(t.t2, t.t3);
                    l.push(this.swapCount && this.needRefund ? t.t4_mixed : (this.swapCount ? t.t4_swap : t.t4_refund));
                    return l;
                },
                sizeCount() { return Object.values(this.sizePv).filter(Boolean).length; },
                cat() { return (this.categories || []).find((c) => c.id === this.category) || null; },
                // the message step, before a topic is picked: the engine's own markup lists the topics (a theme may use a select instead)
                topicListOpen() { return this.step === 'contact' && !!this.categories && this.categories.length > 1 && !this.topicPicked; },
                // the extra form elements of the topic the message goes with
                catFields() { return this.cat ? this.cat.fields : []; },
                country() {
                    const d = (this.order && this.order.orderDeliveries) || [];
                    return (d[0] && d[0].country_id) || (this.order && this.order.delivery_address && this.order.delivery_address.country_id) || null;
                },
                isSwap() { return this.swapCount > 0; },
                shipMethods() {
                    // an exchange: the methods that can bring the new item too (a delivery price), priced as ONE calculation
                    // whether the courier does the exchange natively or not: the outgoing parcel + the return leg (unless the
                    // sale channel pays it). A plain return: the return price, that must not exceed the refund.
                    const list = (this.methods || []).filter((m) => !this.isSwap || m.exchange !== false)
                        .map((m) => (this.isSwap ? { ...m, ...this.swapPrices(m) } : { ...m, list: this.priceOf(m), price: this.priceOf(m) }))
                        .filter((m) => this.isSwap || m.price - this.returnsValue <= 0);
                    // an exchange ships the new item out, so it always needs the courier
                    if (this.selfShip && !this.isSwap) {
                        list.push({ id: 'self', self: true, type: 'self', name: this.t.self_name, desc: this.t.self_desc, price: 0, list: 0 });
                    }
                    return list;
                },
                selfShip() { return !!(this.returnSettings && this.returnSettings.self_ship); },
                isSelf() { return !!this.selected && !!this.selected.self; },
                // the return address lines for "ship it myself"
                selfAddress() {
                    const a = this.returnSettings && this.returnSettings.address;
                    if (!a) return [];
                    return [a.name, [a.post_code, a.city].filter(Boolean).join(' '), [a.street, a.house].filter(Boolean).join(' ')].filter(Boolean);
                },
                // a paid drop-off that costs more than the refund - this flow cannot take payment
                paidMethod() {
                    if (this.isSwap) return null;
                    const all = (this.methods || []).map((m) => ({ ...m, price: this.priceOf(m) })).sort((x, y) => x.price - y.price);
                    return this.shipMethods.length ? null : (all[0] || null);
                },
                selected() { return this.shipMethods.find((m) => m.id === this.method) || null; },
                // an exchange's new parcel goes to a pickup point with a pickup point method: the customer picks the point
                swapPoint() { return !!(this.isSwap && this.selected && !this.selected.self && this.selected.type == 2); },
                // DeliveryMethod type 2 (pickup point) and 9 (drop-off) need no courier pickup
                courier() { return !!this.selected && !this.selected.self && !(this.selected.type == 2 || this.selected.type == 9); },
                hours() {
                    const d = (this.days || []).find((x) => x.date === this.day);
                    if (!d) return [];
                    const out = [];
                    for (let h = d.startTime + 1; h < d.endTime; h++) out.push(h);
                    return out;
                },
                // an exchange's shipping is charged with the order (it is not taken off the refund)
                refundAmount() { return Math.max(0, this.returnsValue - (this.isSwap || !this.selected ? 0 : this.selected.price)); },
                // the shipping of the exchange, the exact quote of the server when there is one for the chosen method
                swapShip() {
                    if (!this.isSwap || !this.selected || this.selected.self) return null;
                    const q = this.quote && this.quote.method === this.selected.id ? this.quote : null;
                    return q || { out: this.selected.out, ret: this.selected.ret, total: this.selected.price, native: !!this.selected.native_exchange, exact: false };
                },
                rfMethod() { return (this.refundMethods || []).find((m) => m.id === this.rf) || null; },
                bankOn() { return !!(this.rfMethod && this.rfMethod.need_bank_details); },
                // domestic account number for HUF, IBAN + BIC otherwise
                bankFields() {
                    return this.currency === 'HUF'
                        ? { holder: this.t.bank_holder, number: this.t.bank_number }
                        : { holder: this.t.bank_holder, iban: this.t.bank_iban, bank_code: this.t.bank_bic };
                },
                shipText() {
                    if (!this.selected) return '';
                    let s = this.selected.name;
                    if (this.courier && this.day && this.hour) s += ' · ' + this.dayLabel(this.day) + ' ' + (this.hour - 1) + ':00–' + this.hour + ':00';
                    return s;
                },
                refundText() {
                    if (!this.rfMethod) return '';
                    return this.rfMethod.need_bank_details ? (this.bank.holder || '') + ' · ' + (this.bank.number || this.bank.iban || '') : this.rfMethod.label;
                }
            },
            methods: {
                fill(tpl, v) { return fill(tpl, v); },
                // the item's variant values and quantity, e.g. "M · Black · 1 pcs"
                meta(it) {
                    return [it.origSize].concat(it.other).filter(Boolean).concat([it.qty + ' ' + (this.t.pcs || 'pcs')]).join(' · ');
                },
                money(v) {
                    const cur = (this.order && this.order.price && this.order.price.currency) || this.currency || this.order?.currency_code || 'HUF';
                    return Money.format(v, cur);
                },
                dayLabel(date) {
                    try { return new Intl.DateTimeFormat(this.lan, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(date + 'T12:00:00')); }
                    catch (e) { return date; }
                },
                reset(order, flow) {
                    this.order = order;
                    this.mode = OrderRules.returnWindow(order) === 'open' ? 'return' : 'dispute';
                    this.items = (order.orderItems || []).filter((oi) => Number(oi.quantity) > 0).map((oi) => toItem(oi, this.lan));
                    const pick = {};
                    this.items.forEach((it) => { pick[it.id] = { on: this.items.length === 1, qty: it.qty, answers: {}, choice: null, pv: null }; });
                    this.pick = pick;
                    this.loadQuestions();
                    this.disputeText = '';
                    this.step = 'items'; this.dir = 1; this.done = false; this.error = ''; this.sending = false;
                    this.methods = null; this.method = null; this.days = []; this.day = null; this.hour = null;
                    this.refundMethods = null; this.rf = null; this.bank = {};
                    const a = order.delivery_address || {};
                    this.addr = AddressFields.blank(a);
                    const d0 = (order.orderDeliveries && order.orderDeliveries[0]) || {};
                    this.daddr = AddressFields.blank(d0);
                    // the country decides the fields (style, county, post code rule)
                    this.addrCountry = null;
                    AddressFields.country(this.country).then((row) => { this.addrCountry = row; });
                    this.sizePv = {}; this.points = null; this.point = null; this.pointQuery = d0.post_code || '';
                    this.categories = null; this.category = null; this.topicPicked = false; this.contactText = ''; this.fields = {}; this.files = [];
                    this.cart = this.items.map((it) => ({
                        key: 'o' + it.id, pv: it.pv, name: it.name, img: it.img, url: it.url,
                        meta: [it.origSize].concat(it.other).filter(Boolean).join(' · '),
                        price: it.price, qty: it.qty, origQty: it.qty, added: false,
                    }));
                    this.q = ''; this.results = []; this.resultCount = 0;
                    this.resetPayment();
                    this.fromMenu = flow === 'menu';
                    this.start(flow || 'return', true);
                },
                start(flow, first) {
                    this.anim = first ? false : canAnimate();
                    this.dir = 1; this.done = false; this.error = '';
                    this.flow = ['return_change', 'dispute'].includes(flow) ? 'return' : (flow === 'point' ? 'address' : flow);
                    this.step = this.steps[0];
                    this.enter();
                },
                // a menu row was clicked: an invoice group opens, a link gets its busy state, an action starts its flow
                menuClick(a, event) {
                    if (a.group) { this.order._invM = !this.order._invM; return; }
                    if (a.href) { if (!a.blank && event) event.currentTarget.classList.add('is-busy'); return; }
                    this.pickAction(a);
                },
                // a drop-off / pickup-point method needs no courier
                isDropOff(m) { return !!m && (m.type == 2 || m.type == 9); },
                // "free" or the price of the method (an exchange: outgoing parcel + return leg)
                shipPrice(m) { return m.price > 0 ? this.money(m.price) : this.t.free; },
                // the cheapest outgoing price of the method for the delivery country (the exchange's new parcel)
                outPriceOf(m) { return this.estimateOf(m.delivery_price); },
                swapPrices(m) {
                    const out = this.outPriceOf(m);
                    const back = this.priceOf(m);
                    const ret = m.return_free ? 0 : back;
                    return { out, ret, list: out + back, price: out + ret };
                },
                // how the exchange goes with this method: one parcel (native) or the new one after the return arrives
                swapKind(m) { return m.native_exchange ? this.t.swap_native : this.t.swap_delayed; },
                // the exact price of the exchange's shipping from the server (region prices, thresholds): the list's price is the estimate
                async loadQuote() {
                    this.quote = null;
                    if (!this.isSwap || !this.selected || this.selected.self || !this.order) return;
                    const m = this.selected;
                    const delivery = { method: m.id, country_id: this.country };
                    if (this.courier && this.addr && this.addr.postcode) delivery.post_code = this.addr.postcode;
                    try {
                        const r = await post('/api/orders/' + this.order.id + '/exchange-quote', { delivery, items: this.swapCount });
                        const d = await r.json();
                        if (d && d.success && d.data && this.selected && this.selected.id === m.id) {
                            this.quote = { method: m.id, out: d.data.outgoing.price, ret: d.data.return.price, total: d.data.total, native: !!d.data.native, exact: true };
                        }
                    } catch (e) { /* the estimate stays */ }
                },
                // how the parcel gets to us with this method
                shipKind(m) { return this.isDropOff(m) ? this.t.drop_off : this.t.pickup; },
                pickAction(a) {
                    if (a.href) { if (a.blank) window.open(a.href, '_blank', 'noopener'); else location.href = a.href; return; }
                    this.start(a.key);
                },
                enter() {
                    if (this.step === 'ship') this.loadMethods();
                    if (this.step === 'refund') this.loadRefund();
                    if (this.step === 'address' && this.pointMode && this.points === null) this.loadPoints();
                    if (this.step === 'contact') this.loadCategories();
                    if (this.step === 'items_pay') this.loadPayments();
                    this.$nextTick(() => { if (this.$refs.body) this.$refs.body.scrollTop = 0; });
                },
                async loadPoints() {
                    const m = this.swapPoint ? this.selected : this.del.delivery_method, cc = this.del.country_code;
                    if (!m || !cc || !this.pointQuery) { this.points = []; return; }
                    this.points = null;
                    try {
                        const r = await fetch('/api/package-points/' + m.id + '/' + cc + '/' + encodeURIComponent(this.pointQuery), { headers: headers() });
                        const d = r.ok ? await r.json() : {};
                        this.points = Array.isArray(d.list) ? d.list : [];
                    } catch (e) { this.points = []; }
                    if (!this.point && !this.swapPoint) this.point = this.points.find((p) => p.pointId === this.del.package_point) || null;
                },
                async loadCategories() {
                    if (this.categories !== null) return;
                    try {
                        // type 2 = the order problem topics (type 1 is the general customer service form's list)
                        const r = await post('/api/contact-categories', { type: 2 });
                        const d = r.ok ? await r.json() : {};
                        this.categories = (d.data || []).map((c) => {
                            const tr = c.contactCategoryTranslations || [];
                            return { id: c.id, name: ((tr.find((x) => x.language_code === this.lan) || tr.find((x) => x.language_code === 'en') || tr[0] || {}).name) || '', fields: c.fields || [], allowFiles: !!c.allow_files };
                        });
                        if (this.categories.length && !this.category) this.category = this.categories[0].id;
                        // one topic (or none): nothing to choose
                        if (this.categories.length < 2) this.topicPicked = true;
                    } catch (e) { this.categories = []; this.topicPicked = true; this.error = this.t.e_load; }
                },
                // a topic was picked from the list: its form follows (the topic's own fields and files start empty)
                pickTopic(c) { this.category = c.id; this.fields = {}; this.files = []; this.topicPicked = true; this.error = ''; },
                changeTopic() { this.topicPicked = false; this.error = ''; },
                fieldLabel(f) { const l = f.labels || {}; return l[this.lan] || l.en || Object.values(l)[0] || f.key; },
                optionLabel(o) { const l = o.labels || {}; return l[this.lan] || l.en || Object.values(l)[0] || o.value; },
                pickFiles(e) { this.files = Array.from(e.target.files).slice(0, 3); },
                toggle(it) { this.pick[it.id].on = !this.pick[it.id].on; this.error = ''; },
                qty(it, d) { const p = this.pick[it.id]; p.qty = Math.min(it.qty, Math.max(1, p.qty + d)); },
                // the return form of Prodyflow settings/returns
                async loadQuestions() {
                    if (this.questions.length) return;
                    try {
                        const r = await post('/api/orders/return-questions');
                        const d = await r.json();
                        this.questions = (d && d.success && Array.isArray(d.data)) ? d.data : [];
                    } catch (e) { this.questions = []; }
                },
                answer(it, q) {
                    const a = this.pick[it.id].answers;
                    if (!a[q.id]) a[q.id] = { option_id: null, text: '' };
                    return a[q.id];
                },
                setOption(it, q, o) { this.answer(it, q).option_id = o.id; this.pruneAnswers(it); this.error = ''; },
                // a question that depends (show_if) on an earlier answer: asked only when that question is asked and got one of the options
                qVisible(it, q, depth = 0) {
                    const c = q.show_if;
                    if (!c || !c.question_id || depth > 10) return true;
                    const parent = this.questions.find((x) => x.id === c.question_id);
                    if (!parent || !this.qVisible(it, parent, depth + 1)) return false;
                    const a = this.pick[it.id].answers[parent.id];
                    return !!a && (c.option_ids || []).includes(a.option_id);
                },
                visibleQuestions(it) { return this.questions.filter((q) => this.qVisible(it, q)); },
                // the answers of the questions that are not asked (any more) are dropped
                pruneAnswers(it) {
                    const a = this.pick[it.id].answers;
                    this.questions.forEach((q) => { if (a[q.id] && !this.qVisible(it, q)) delete a[q.id]; });
                },
                chosen(it) {
                    const a = this.pick[it.id].answers;
                    return this.questions.flatMap((q) => (q.options || []).filter((o) => a[q.id] && a[q.id].option_id === o.id));
                },
                // an answer with the change_variant action: offer another variant instead of a refund
                wantsVariant(it) { return this.chosen(it).some((o) => o.action === 'change_variant'); },
                // an answer with the replace_same action: the exchange of the same product (the same variant, a new piece)
                wantsSame(it) { return this.chosen(it).some((o) => o.action === 'replace_same'); },
                needsOffer(it) { return this.exchange && (!this.hasActions || this.wantsVariant(it) || this.wantsSame(it)); },
                offer(it) {
                    if (this.wantsSame(it) && !this.wantsVariant(it)) return { title: this.t.same_product, sub: this.t.sub_same };
                    return { title: this.t.other_size, sub: this.wantsVariant(it) ? this.t.sub_free : this.t.sub_pick };
                },
                swapLabel(it) {
                    const p = this.pick[it.id];
                    const s = it.sizes.find((x) => x.pv === p.pv) || null;
                    if (!s) return this.t.swap_any;
                    return s.orig ? this.t.swap_new : fill(this.t.swap_to, s.label);
                },
                // the same variant (a new piece of it) only with the replace_same answer, the other variants with change_variant;
                // a form without such options keeps allowing every available variant
                canPick(it, s) {
                    if (!s.ok) return false;
                    if (!this.hasActions) return true;
                    return s.orig ? this.wantsSame(it) : this.wantsVariant(it);
                },
                choose(it, c) {
                    const p = this.pick[it.id];
                    p.choice = c;
                    if (c === 'swap' && !p.pv && (this.wantsVariant(it) || this.wantsSame(it))) {
                        const first = it.sizes.find((s) => this.canPick(it, s) && !s.orig) || it.sizes.find((s) => this.canPick(it, s));
                        if (first) p.pv = first.pv;
                    }
                    this.error = '';
                },
                lineFor(it) {
                    if (this.mode === 'dispute') return this.t.l_dispute;
                    if (!this.swaps.includes(it)) {
                        const why = this.chosen(it).map((o) => o.text).filter(Boolean).join(', ');
                        return this.t.l_ret + (why ? ' · ' + why : '');
                    }
                    const s = it.sizes.find((x) => x.pv === this.pick[it.id].pv);
                    return s && s.orig ? this.t.l_new : fill(this.t.l_swap, s ? s.label : '');
                },
                // no exchange in this channel: point to the product for a new order
                tipFor(it) {
                    if (!this.wantsVariant(it) || !it.url) return null;
                    return { text: this.t.tip_variant };
                },
                openProduct(it) {
                    if (it && it.url) location.href = it.url;
                },
                /* ── changing the products ─────────────────────────────────── */
                cartQty(c, d) {
                    c.qty = Math.min(99, Math.max(0, c.qty + d));
                    if (c.added && c.qty === 0) this.cart = this.cart.filter((x) => x !== c);
                    this.error = '';
                },
                cartRemove(c) {
                    if (c.added) this.cart = this.cart.filter((x) => x !== c);
                    else c.qty = 0;
                    this.error = '';
                },
                cartRestore(c) { c.qty = c.origQty; this.error = ''; },
                async search() {
                    const q = String(this.q || '').trim();
                    if (q.length < 2) { this.results = []; this.resultCount = 0; return; }
                    this.searching = true;
                    try {
                        const r = await fetch('/api/search?is_help_page=1', { method: 'POST', headers: headers(), body: JSON.stringify({ q }) });
                        const d = await r.json();
                        if (String(this.q || '').trim() !== q) return; // a newer search is running
                        this.results = (d.data || []).map((item) => ({
                            pv: item.id,
                            name: (item.product && item.product.name) || '',
                            img: (item.image && item.image.src) || '',
                            price: Number(item.product_discount_prices ?? item.product_prices) || 0,
                            meta: (item.characteristics || []).map((c) => c.value || c.val_text).filter(Boolean).join(' · '),
                            inStock: !!item.in_stock,
                        }));
                        this.resultCount = d.count || this.results.length;
                    } catch (e) { this.results = []; this.resultCount = 0; this.error = this.t.e_load; }
                    this.searching = false;
                },
                cartAdd(res) {
                    if (!res.inStock) return;
                    const hit = this.cart.find((c) => String(c.pv) === String(res.pv));
                    if (hit) { hit.qty += 1; }
                    else {
                        this.cart.push({ key: 'n' + res.pv, pv: res.pv, name: res.name, img: res.img, url: null, meta: res.meta, price: res.price, qty: 1, origQty: 0, added: true });
                    }
                    this.q = ''; this.results = []; this.resultCount = 0;
                    this.error = '';
                },
                // the order's own delivery (country + method) - the payment methods depend on it
                orderDelivery() {
                    const d = (this.order && this.order.orderDeliveries) || [];
                    const home = d.find((x) => !x.package_point) || d[0] || {};
                    return { country_id: home.country_id, method: (d[0] && (d[0].delivery_method && d[0].delivery_method.id)) || (d[0] && d[0].delivery_method_id) || null };
                },
                async loadPayments() {
                    if (this.payments !== null) return;
                    try {
                        const r = await fetch('/api/payment-methods', { method: 'POST', headers: headers(), body: JSON.stringify(this.orderDelivery()) });
                        const d = r.ok ? await r.json() : [];
                        this.payments = Array.isArray(d) ? d : (d.data || []);
                    } catch (e) { this.payments = []; this.error = this.t.e_load; }
                },
                resetPayment() {
                    this.payments = null; this.payment = null; this.payError = ''; this.payReady = false; this.paymentData = null;
                    this.payEvents = { start: null, finish: null, finishButton: null };
                    const box = document.getElementById('order-manage-payment');
                    if (box) box.innerHTML = '';
                },
                // an online payment: start it for the difference, the payment app renders its form
                async setPayment(p) {
                    this.payment = p.id; this.error = ''; this.payError = ''; this.payReady = false;
                    this.payEvents = { start: null, finish: null, finishButton: null };
                    const box = document.getElementById('order-manage-payment');
                    if (box) box.innerHTML = '';
                    if (Number(p.type) !== 1) return;
                    try {
                        const r = await post('/api/orders/' + this.order.id + '/payment', { payment_method: p.id, amount: this.itemsDiff });
                        const d = await r.json();
                        if (d.code !== 200 || !d.payment) { this.payError = d.message || this.t.e_send; return; }
                        this.paymentData = d.payment;
                        claimCart(this);
                        const sc = d.script || {};
                        if (sc.url && sc.script) loadScript(sc.url, '/api/script/' + sc.id + '.js');
                        else if (sc.url) loadScript(sc.url);
                        else if (sc.script) loadScript('/api/script/' + sc.id + '.js');
                    } catch (e) { this.payError = this.t.e_send; }
                },
                // called by the payment app (window.CART, see claimCart)
                payStart(event) {
                    this.payEvents.start = event;
                    const info = this.paymentData?.original?.data ?? this.paymentData?.data ?? this.paymentData;
                    if (event && info && info.payment_method_id !== undefined) {
                        event(info, 'order-manage-payment');
                        this.payReady = true;
                    }
                },
                payFinish(event) { this.payEvents.finish = event; this.payReady = true; },
                payFinishButton(event) { this.payEvents.finishButton = event; this.payReady = true; },
                async takePayment() {
                    const info = this.paymentData?.original?.data ?? this.paymentData?.data ?? this.paymentData;
                    if (this.payEvents.finish) return this.payEvents.finish();
                    if (this.payEvents.finishButton) return this.payEvents.finishButton(info);
                    throw new Error(this.t.e_pay_form);
                },
                async submitItems() {
                    if (this.itemsDiff > 0 && this.onlinePayment) {
                        try { await this.takePayment(); }
                        catch (err) { this.payError = (err && err.message) || this.t.e_pay_form; throw new Error(this.payError); }
                    }
                    if (this.itemsDiff < 0 && this.paid && this.rfMethod) await this.sendRefundMethod();
                    const add = this.itemsAdd, del = this.itemsDel;
                    const delivery = this.orderDelivery();
                    let url = '/api/orders/item/' + this.order.id + '/change';
                    let body = { operation: 'change', productsToAdd: add, productsToDel: del, delivery };
                    // the add route is the checkout's: it is authorised with the order's own token, not the login
                    if (!del.length) { url = '/api/orders/item/' + this.order.id + '/add'; body = { operation: 'add', productsToAdd: add, delivery, token: this.order.token }; }
                    else if (!add.length) { url = '/api/orders/item/' + this.order.id + '/remove'; body = { operation: 'delete', productsToDel: del, delivery }; }
                    const r = await post(url, body);
                    const d = await r.json().catch(() => ({}));
                    if (!d.success) throw new Error(this.errText(d));
                },

                // The estimate of a price list for the delivery country (the server's quote has the exact one). A row with a
                // condition (free above an order value, a region, a piece count) is NOT the usual price: the cheapest of all
                // rows would be the "free over 40 000" one, 0. The usual (unconditional) rows give the estimate; only
                // when there is none, the cheapest of the others.
                estimateOf(rows) {
                    const own = (rows || []).filter((p) => p.country_id == null || p.country_id == this.country);
                    const usual = own.filter((p) => !p.min_order_amount && !p.min_order_count && !p.post_code_regex);
                    const pool = usual.length ? usual : own;
                    return pool.reduce((min, p) => (min === null || Number(p.price) < min ? Number(p.price) : min), null) || 0;
                },
                priceOf(m) { return this.estimateOf(m.price); },
                async loadMethods() {
                    if (this.methods !== null || !this.country) return;
                    const settings = this.loadReturnSettings();
                    try {
                        const r = await fetch('/api/countries/deliveries/' + this.country + '/returns', { headers: headers() });
                        this.methods = r.ok ? await r.json() : [];
                    } catch (e) { this.methods = []; this.error = this.t.e_load; }
                    await settings;
                    if (!this.method && this.shipMethods.length) this.setMethod(this.shipMethods.find((m) => m.price === 0) || this.shipMethods[0]);
                },
                async loadReturnSettings() {
                    if (this.returnSettings !== null) return;
                    try {
                        const r = await post('/api/orders/return-settings');
                        const d = await r.json();
                        this.returnSettings = (d && d.success && d.data) ? d.data : {};
                    } catch (e) { this.returnSettings = {}; }
                },
                setMethod(m) {
                    this.method = m.id; this.error = ''; this.loadQuote(); if (this.courier) this.loadDays();
                    if (this.isSwap) {
                        this.point = null; this.points = null;
                        if (this.swapPoint) { if (!this.pointQuery) this.pointQuery = (this.addr && this.addr.postcode) || (this.del && this.del.post_code) || ''; this.loadPoints(); }
                    }
                },
                delivery() {
                    if (this.isSelf) return { country_id: this.country };
                    const d = { country_id: this.country, method: this.method };
                    if (this.courier) Object.assign(d, this.addrBody(this.addr), { package_point: '' });
                    if (this.swapPoint && this.point) Object.assign(d, { package_point: this.point.pointId, post_code: this.point.postCode, city: this.point.city, street: this.point.street, house: '' });
                    return d;
                },
                addrDone(a) { return AddressFields.isComplete(this.addrCountry, a); },
                // the API's field names
                addrBody(a) { return { post_code: a.postcode || '', city: a.city || '', street: a.street || '', house: a.house || '', state: a.state || '', area: a.area || '' }; },
                async loadDays() {
                    if (this.isSwap) this.loadQuote();
                    if (!this.courier || !this.addrDone(this.addr)) { this.days = []; return; }
                    this.days = null;
                    try {
                        const r = await post('/api/package-pickups', this.delivery());
                        const d = r.ok ? await r.json() : [];
                        this.days = Array.isArray(d) ? d : [];
                    } catch (e) { this.days = []; }
                    if (!this.days.some((x) => x.date === this.day)) { this.day = null; this.hour = null; }
                },
                async loadRefund() {
                    if (this.refundMethods !== null) return;
                    try {
                        const r = await post('/api/orders/' + this.order.id + '/returnmethods');
                        const d = await r.json();
                        const data = d.data || {};
                        const L = String(this.lan).toUpperCase();
                        this.currency = data.currency || '';
                        this.refundMethods = (data.methods || []).map((m) => {
                            const tr = (m.returnMethodsTranslations || []).map((x) => x[L] || Object.values(x)[0]).find(Boolean) || {};
                            return { ...m, label: tr.name || '' };
                        });
                        const b = data.bank_details && !Array.isArray(data.bank_details) ? data.bank_details : {};
                        this.bank = { holder: b.holder || '', number: b.number || '', iban: b.iban || '', bank_code: b.bank_code || '', transit_code: b.transit_code || '' };
                        if (!this.rf && this.refundMethods.length) this.rf = (this.refundMethods.find((m) => !m.need_bank_details) || this.refundMethods[0]).id;
                    } catch (e) { this.refundMethods = []; this.error = this.t.e_load; }
                },
                validate() {
                    const s = this.step, t = this.t;
                    if (s === 'items') {
                        if (!this.picked.length) return t.e_pick;
                        if (this.mode === 'dispute') return this.disputeText.trim().length < 20 ? t.e_dispute : '';
                        const missing = (it, q) => {
                            const a = this.pick[it.id].answers[q.id];
                            return q.type === 'selectable' ? !(a && a.option_id) : !(a && String(a.text || '').trim());
                        };
                        if (this.picked.some((it) => this.visibleQuestions(it).some((q) => q.is_required && missing(it, q)))) return t.e_reason;
                    }
                    if (s === 'offer') {
                        if (this.offerItems.some((it) => !this.pick[it.id].choice)) return t.e_choice;
                        if (this.picked.some((it) => this.pick[it.id].choice === 'swap' && !this.pick[it.id].pv)) return t.e_size;
                    }
                    if (s === 'ship') {
                        if (!this.selected) return t.e_method;
                        if (this.swapPoint && !this.point) return t.e_point;
                        if (this.courier) {
                            if (!this.addrDone(this.addr)) return t.e_addr;
                            if ((this.days || []).length && (!this.day || !this.hour)) return t.e_time;
                        }
                    }
                    if (s === 'address') {
                        if (this.pointMode) { if (!this.point) return t.e_point; }
                        else if (!this.addrDone(this.daddr)) return t.e_addr2;
                    }
                    if (s === 'size' && !this.sizeCount) return t.e_size;
                    if (s === 'items_add' && !this.itemsChanged) return t.e_items;
                    if (s === 'items_pay') {
                        if (!this.paymentMethod) return t.e_payment;
                        if (this.onlinePayment && !this.payReady) return t.e_pay_form;
                    }
                    if (s === 'contact' && this.contactText.trim().length < 5) return t.e_contact;
                    if (s === 'contact' && !this.category) return t.e_load;
                    if (s === 'contact' && this.catFields.some((f) => f.required && !String(this.fields[f.key] || '').trim())) return t.e_field;
                    if (s === 'refund') {
                        if (!this.rfMethod) return t.e_refund;
                        if (this.bankOn && Object.keys(this.bankFields).some((k) => !String(this.bank[k] || '').trim())) return t.e_bank;
                    }
                    return '';
                },
                go(d) {
                    const i = this.stepIndex + d;
                    // back from a flow's first step to the menu it was started from
                    if (i < 0 && this.fromMenu && this.flow !== 'menu') { this.start('menu'); this.dir = -1; return; }
                    if (i < 0 || i >= this.steps.length) return;
                    this.anim = canAnimate();
                    this.error = '';
                    this.dir = d;
                    this.step = this.steps[i];
                    this.enter();
                },
                canBack() { return !this.done && this.flow !== 'menu' && (this.stepIndex > 0 || this.fromMenu); },
                next() {
                    const e = this.validate();
                    if (e) { this.error = e; return; }
                    if (this.flow === 'return') { if (this.step === 'summary') this.submit(); else this.go(1); return; }
                    if (this.isLast) this.submitOther(); else this.go(1);
                },
                finish() {
                    releaseCart();
                    this.dir = 1; this.anim = canAnimate(); this.done = true;
                    document.dispatchEvent(new CustomEvent('order-manage:done', { detail: { order: this.order, flow: this.flow } }));
                },
                async sendRefundMethod() {
                    const r = await post('/api/orders/' + this.order.id + '/refound', {
                        return_methods_id: this.rf,
                        crmItemBankAccount: {
                            holder: this.bank.holder || null, number: this.bank.number || null, bank_code: this.bank.bank_code || null,
                            iban: this.bank.iban || null, transit_code: this.bank.transit_code || null
                        }
                    });
                    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(this.errText(d)); }
                },
                // error answers: `error` string, `errors` per field, or `message`
                errText(d) {
                    if (d && typeof d.error === 'string' && d.error !== 'OK') return d.error;
                    if (d && d.errors) { const v = Object.values(d.errors).flat(); if (v.length) return String(v[0]); }
                    return (d && d.message && d.message.trim()) || '';
                },
                async submitOther() {
                    this.sending = true;
                    this.error = '';
                    const o = this.order;
                    try {
                        if (this.flow === 'address') {
                            const d0 = this.del;
                            const body = { country_id: d0.country_id, country_code: d0.country_code || '', method: d0.delivery_method && d0.delivery_method.id };
                            if (this.pointMode) Object.assign(body, { package_point: this.point.pointId, post_code: this.point.postCode, city: this.point.city, street: this.point.street, house: '', state: '', area: '' });
                            else Object.assign(body, this.addrBody(this.daddr), { package_point: '' });
                            const r = await post('/api/orders/' + o.id + '/delivery/edit', body);
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok || d.success === false) throw new Error(this.errText(d));
                        } else if (this.flow === 'size') {
                            const items = this.items.filter((it) => this.sizePv[it.id]);
                            const body = {
                                operation: 'change',
                                productsToAdd: items.map((it) => ({ id: this.sizePv[it.id], quantity: it.qty })),
                                productsToDel: items.map((it) => ({ id: it.pv, quantity: it.qty })),
                                delivery: { country_id: this.country, method: null }
                            };
                            const r = await post('/api/orders/item/' + o.id + '/change', body);
                            const d = await r.json().catch(() => ({}));
                            if (!d.success) throw new Error(this.errText(d));
                        } else if (this.flow === 'items') {
                            await this.submitItems();
                        } else if (this.flow === 'cancel') {
                            const r = await post('/api/orders/' + o.id + '/deny', { operation: 'delete' });
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok || d.success === false) throw new Error(this.errText(d));
                            if (this.paid && this.rfMethod) await this.sendRefundMethod();
                        } else if (this.flow === 'refund') {
                            await this.sendRefundMethod();
                        } else if (this.flow === 'contact') {
                            // multipart: the topic's extra answers and files travel with the message
                            const body = new FormData();
                            body.append('token', token() || '');
                            body.append('contact_category_id', this.category);
                            body.append('text', this.contactText.trim());
                            body.append('order_id', o.id);
                            body.append('order_token', o.token || '');
                            if (this.catFields.length) body.append('fields', JSON.stringify(this.fields));
                            if (this.cat && this.cat.allowFiles) this.files.forEach((f) => body.append('files[]', f));
                            const h = headers();
                            delete h['Content-Type'];
                            const r = await fetch('/api/contact', { method: 'POST', headers: h, body });
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok) throw new Error(this.errText(d));
                        }
                        this.finish();
                    } catch (err) {
                        this.error = (err && err.message) || this.t.e_send;
                    }
                    this.sending = false;
                },
                async submit() {
                    this.sending = true;
                    this.error = '';
                    try {
                        if (this.needRefund && this.rfMethod) await this.sendRefundMethod();
                        // a return / late return sends the items back: the backend must not cancel them like unsent ones
                        const productsToDel = this.picked.map((it) => ({ id: it.pv, quantity: this.pick[it.id].qty, return: true }));
                        const productsToAdd = this.swaps.map((it) => ({ id: this.pick[it.id].pv, quantity: this.pick[it.id].qty }));
                        const body = productsToAdd.length
                            ? { operation: 'change', productsToAdd, productsToDel, delivery: this.delivery() }
                            : { operation: 'delete', productsToDel, delivery: this.delivery() };
                        if (this.courier && this.day && this.hour) body.pickup = { date: this.day, time: this.hour };
                        if (this.isSelf) body.self_ship = 1;
                        if (this.mode === 'dispute') body.dispute_message = this.disputeText.trim();
                        body.answers = this.picked.flatMap((it) => this.visibleQuestions(it).map((q) => {
                            const a = this.pick[it.id].answers[q.id] || {};
                            return { order_item_id: it.id, question_id: q.id, option_id: a.option_id || null, text: String(a.text || '').trim() };
                        })).filter((a) => a.option_id || a.text);
                        const r = await post('/api/orders/item/' + this.order.id + (productsToAdd.length ? '/change' : '/remove'), body);
                        const d = await r.json().catch(() => ({}));
                        if (!d.success) throw new Error(this.errText(d));
                        this.finish();
                    } catch (err) {
                        this.error = (err && err.message) || this.t.e_send;
                    }
                    this.sending = false;
                },
                close() {
                    releaseCart();
                    if (hooks.close) { hooks.close(); return; }
                    const dlg = mountEl && mountEl.closest('dialog');
                    if (dlg && dlg.open) dlg.close();
                },
                closeDone() {
                    this.close();
                    // the Orders list shows the new state
                    try { ordersRoot().fetchOrders(); } catch (e) { /* no list on this page */ }
                }
            }
        };
    };

    const mount = () => {
        if (root) return root;
        const el = document.querySelector('[data-order-manage-app]');
        if (!el || typeof Vue === 'undefined') return null;
        const comp = component(el);
        comp.delimiters = (el.dataset.delimiters || '[[,]]').split(',');
        app = Vue.createApp(comp);
        app.component('address-fields', AddressFields);
        root = app.mount(el);
        mountEl = el;
        document.dispatchEvent(new CustomEvent('order-manage:mounted', { detail: { element: el } }));
        return root;
    };

    // open an order (an order object, or its id) in the dialog:
    // flow = menu | return | address | size | cancel | refund | contact
    const open = async (orderOrId, flow) => {
        const r = mount();
        if (!r) return false;
        const order = (orderOrId && typeof orderOrId === 'object')
            ? JSON.parse(JSON.stringify(orderOrId))
            : await findOrder(orderOrId).catch(() => null);
        if (!order) return false;
        r.reset(order, flow || 'menu');
        if (hooks.open) hooks.open(); else {
            const dlg = mountEl && mountEl.closest('dialog');
            if (dlg && !dlg.open) dlg.showModal();
        }
        return true;
    };

    document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-return-open], [data-order-manage]');
        if (!btn || !btn.dataset.orderId) return;
        e.preventDefault();
        btn.classList.add('is-busy');
        const flow = btn.dataset.flow || (btn.hasAttribute('data-order-manage') ? 'menu' : 'return');
        await open(btn.dataset.orderId, flow);
        btn.classList.remove('is-busy');
    });

    const boot = () => setTimeout(mount, 0);
    if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', boot); else boot();

    return {
        setup(h) { hooks = Object.assign({}, hooks, h || {}); return root; },
        mount, open,
        // whether the page has the dialog markup (a theme may not have it)
        available() { return !!document.querySelector('[data-order-manage-app]') && typeof Vue !== 'undefined'; },
        // the labelled action list of an order (the same as the dialog's menu)
        actions(order) {
            const el = document.querySelector('[data-order-manage-app]');
            if (!el) return [];
            const t = root ? root.t : readTexts(el);
            const cfg = { isChange: el.dataset.isChange === '1', isReturn: el.dataset.isReturn === '1' };
            return OrderRules.actions(order, cfg).map((a) => Object.assign({}, a, { label: t['m_' + a.key] || a.key, sub: a.detail || t['m_' + a.key + '_s'] || '' }));
        },
        get app() { return root; },
    };
})();

window.OrderManage = OrderManage;
